<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';
import {
  hasSelectedInbox,
  isInboxSelected,
  toggleInboxSelection,
} from 'dashboard/mostChatwootExtensions/cannedResponseAccess';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';

export default {
  name: 'AddCanned',
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
  },
  props: {
    responseContent: {
      type: String,
      default: '',
    },
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      shortCode: '',
      content: this.responseContent || '',
      inboxIds: [],
      addCanned: {
        showLoading: false,
        message: '',
      },
      show: true,
    };
  },
computed: {
  ...mapGetters({
    inboxes: "inboxes/getInboxes",
  }),
  hasSelectedInbox() {
    return hasSelectedInbox(this.inboxIds);
  },
},
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
    content: {
      required,
    },
  },
  methods: {
toggleInbox(inboxId) {
  this.inboxIds = toggleInboxSelection(this.inboxIds, inboxId);
},
isInboxSelected(inboxId) {
  return isInboxSelected(this.inboxIds, inboxId);
},
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.inboxIds = [];
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
addCannedResponse() {
  if (!this.hasSelectedInbox) {
    useAlert("Selecione pelo menos uma caixa postal.");
    return;
  }

      // Show loading on button
      this.addCanned.showLoading = true;
      // Make API Calls
      this.$store
        .dispatch('createCannedResponse', {
          short_code: this.shortCode,
          content: this.content,
          inbox_ids: this.inboxIds,
        })
        .then(() => {
          // Reset Form, Show success message
          this.addCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.ADD.API.SUCCESS_MESSAGE'));
          this.resetForm();
          this.onClose();
        })
        .catch(error => {
          this.addCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.ADD.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('CANNED_MGMT.ADD.TITLE')"
        :header-content="$t('CANNED_MGMT.ADD.DESC')"
      />
      <form class="flex flex-col w-full" @submit.prevent="addCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.ADD.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.SHORT_CODE.PLACEHOLDER')"
              @blur="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.ADD.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
        </div>
        <div class="w-full mt-4">
  <p class="mb-2 text-sm font-medium text-n-slate-12">
    Disponível nas caixas
  </p>

  <div class="flex flex-col gap-2">
    <button
      v-for="inbox in inboxes"
      :key="inbox.id"
      type="button"
      class="p-2 rounded-md border border-solid flex items-center justify-between text-start"
      :class="
        isInboxSelected(inbox.id)
          ? 'bg-n-blue-2 dark:bg-n-blue-1 border-n-blue-3 dark:border-n-blue-4'
          : 'bg-white dark:bg-n-solid-2 border-n-weak dark:border-n-strong'
      "
      @click="toggleInbox(inbox.id)"
    >
      <span class="text-sm text-n-slate-12">
        {{ inbox.name }}
      </span>

      <span v-if="isInboxSelected(inbox.id)">✓</span>
    </button>
  </div>

  <p v-if="!hasSelectedInbox" class="mt-2 text-sm text-n-ruby-9">
    Selecione pelo menos uma caixa postal.
  </p>
</div>

<div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.ADD.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.ADD.FORM.SUBMIT')"
            :disabled="
              v$.content.$invalid ||
              v$.shortCode.$invalid ||
              !hasSelectedInbox ||
              addCanned.showLoading
            "
            :is-loading="addCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
:deep(.ProseMirror-menubar) {
  @apply hidden;
}

:deep(.ProseMirror-woot-style) {
  @apply min-h-[12.5rem];
}
</style>
